'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useUser } from '@clerk/nextjs';
import { sendGAEventSafe } from '@/lib/ga';

const SIGN_UP_INTENT_KEY = 'ga_signup_intent';
const GA_REGISTRATION_SENT_KEY = 'ga_registration_sent';

export default function GASignUpTracker() {
  const pathname = usePathname();
  const { isSignedIn, user, isLoaded } = useUser();
  const hasTrackedRegistration = useRef(false);

  // /sign-up, /sign-in 페이지 방문 시 가입 의도 플래그 세팅
  useEffect(() => {
    if (pathname?.startsWith('/sign-up') || pathname?.startsWith('/sign-in')) {
      sessionStorage.setItem(SIGN_UP_INTENT_KEY, 'true');
    }
  }, [pathname]);

  // 회원가입 완료 감지 및 GA 이벤트 전송
  useEffect(() => {
    if (!isLoaded || hasTrackedRegistration.current) return;

    if (isSignedIn && user) {
      const hadSignUpIntent = sessionStorage.getItem(SIGN_UP_INTENT_KEY) === 'true';
      const createdAt = user.createdAt;
      const createdTime = createdAt ? new Date(createdAt).getTime() : 0;
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      const isNewUser = createdTime > 0 && now - createdTime < fiveMinutes;
      const alreadySent = typeof window !== 'undefined' && localStorage.getItem(GA_REGISTRATION_SENT_KEY) === 'true';

      if (process.env.NODE_ENV === 'development') {
        console.log('[GA] Sign-up check:', {
          hadSignUpIntent,
          isNewUser,
          createdAt,
          timeDiff: createdTime > 0 ? `${Math.round((now - createdTime) / 1000)}s` : 'N/A',
          alreadySent,
        });
      }

      if (hadSignUpIntent && isNewUser && !alreadySent) {
        sendGAEventSafe('event', 'sign_up', {
          method: user.externalAccounts?.[0]?.provider || 'email',
        });

        localStorage.setItem(GA_REGISTRATION_SENT_KEY, 'true');
        hasTrackedRegistration.current = true;
        sessionStorage.removeItem(SIGN_UP_INTENT_KEY);

        if (process.env.NODE_ENV === 'development') {
          console.log('[GA] Sign-up conversion sent:', user.id);
        }
      }
    }
  }, [isLoaded, isSignedIn, user]);

  return null;
}
